import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Users, PoundSterling, TrendingUp, ChevronDown, ChevronUp, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const AdminRevenue = ({ revenue, formatDate }) => {
  const [revenueExpanded, setRevenueExpanded] = useState(false);
  const [revenueDetails, setRevenueDetails] = useState(null);
  const [revYear, setRevYear] = useState(new Date().getFullYear());
  const [revMonth, setRevMonth] = useState(null);
  const [loadingRevDetails, setLoadingRevDetails] = useState(false);
  const [expandedSpendUser, setExpandedSpendUser] = useState(null);

  const fetchRevenueDetails = async (y, m) => {
    setLoadingRevDetails(true);
    try {
      const params = new URLSearchParams({ year: y });
      if (m !== null && m !== 'all') params.append('month', m);
      const res = await axios.get(`${API_URL}/api/admin/revenue/details?${params}`);
      setRevenueDetails(res.data);
    } catch (err) {
      toast.error('Failed to load revenue details');
    } finally {
      setLoadingRevDetails(false);
    }
  };

  const handleRevenueExpand = () => {
    const next = !revenueExpanded;
    setRevenueExpanded(next);
    if (next && !revenueDetails) {
      fetchRevenueDetails(revYear, revMonth);
    }
  };

  const handleRevYearChange = (val) => {
    const y = parseInt(val);
    setRevYear(y);
    setRevMonth(null);
    fetchRevenueDetails(y, null);
  };

  const handleRevMonthChange = (val) => {
    const m = val === 'all' ? null : parseInt(val);
    setRevMonth(m);
    fetchRevenueDetails(revYear, m);
  };

  if (!revenue) return null;

  return (
    <>
      <div
        className={`grid grid-cols-1 sm:grid-cols-2 gap-3 cursor-pointer transition-all ${revenueExpanded ? 'ring-1 ring-amber-500/20 rounded-lg' : ''}`}
        onClick={handleRevenueExpand}
        data-testid="revenue-section"
      >
        <Card className="bg-gradient-to-br from-slate-800 to-slate-800/80 border-amber-500/30 hover:border-amber-500/50 transition-colors">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">This Month</p>
              <div className="flex items-center gap-2">
                <PoundSterling className="h-4 w-4 text-amber-400" />
                {revenueExpanded ? <ChevronUp className="h-3.5 w-3.5 text-amber-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-500" />}
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-white" data-testid="monthly-revenue">
              £{revenue.monthly_revenue.toFixed(2)}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {revenue.monthly_transactions} transaction{revenue.monthly_transactions !== 1 ? 's' : ''} — {revenue.period.month}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-slate-800 to-slate-800/80 border-emerald-500/30 hover:border-emerald-500/50 transition-colors">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] sm:text-xs text-slate-500 uppercase tracking-wider">Annual Revenue</p>
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-emerald-400" />
                {revenueExpanded ? <ChevronUp className="h-3.5 w-3.5 text-emerald-400" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-500" />}
              </div>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-white" data-testid="annual-revenue">
              £{revenue.annual_revenue.toFixed(2)}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {revenue.annual_transactions} transaction{revenue.annual_transactions !== 1 ? 's' : ''} — {revenue.period.year}
            </p>
            {(revenue.plan_breakdown.monthly > 0 || revenue.plan_breakdown.yearly > 0) && (
              <div className="flex gap-3 mt-2 text-[10px] text-slate-500">
                <span>Monthly plans: £{revenue.plan_breakdown.monthly.toFixed(2)}</span>
                <span>Yearly plans: £{revenue.plan_breakdown.yearly.toFixed(2)}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Revenue Details — Expanded */}
      {revenueExpanded && (
        <Card className="bg-slate-800 border-amber-500/30" data-testid="revenue-details-panel">
          <CardHeader className="pb-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
                <Users className="h-4 w-4 text-amber-400" />
                User Signups & Spending
              </CardTitle>
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <select
                  value={revYear}
                  onChange={(e) => handleRevYearChange(e.target.value)}
                  className="bg-slate-900 border border-slate-600 text-white text-sm rounded-md px-2 h-8 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  data-testid="revenue-year-select"
                >
                  {(revenueDetails?.available_years || [new Date().getFullYear()]).map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <select
                  value={revMonth === null ? 'all' : revMonth}
                  onChange={(e) => handleRevMonthChange(e.target.value)}
                  className="bg-slate-900 border border-slate-600 text-white text-sm rounded-md px-2 h-8 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  data-testid="revenue-month-select"
                >
                  <option value="all">All months</option>
                  {MONTH_NAMES.map((name, i) => (
                    <option key={i + 1} value={i + 1}>{name}</option>
                  ))}
                </select>
              </div>
            </div>
            {revenueDetails && (
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                <span>{revenueDetails.user_count} user{revenueDetails.user_count !== 1 ? 's' : ''} signed up</span>
                <span>Total lifetime spend: <strong className="text-amber-400">£{revenueDetails.total_spent_by_filtered.toFixed(2)}</strong></span>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {loadingRevDetails ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-500" />
              </div>
            ) : revenueDetails && revenueDetails.users.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-6">No users signed up in this period</p>
            ) : revenueDetails && (
              <div className="divide-y divide-slate-700">
                {revenueDetails.users.map((u) => (
                  <div key={u.id} className="p-3 sm:p-4" data-testid={`revenue-user-${u.id}`}>
                    <div
                      className="flex items-center justify-between cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); setExpandedSpendUser(expandedSpendUser === u.id ? null : u.id); }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white text-sm font-medium">{u.name}</p>
                          {u.role === 'superadmin' && <Badge className="bg-rose-500/20 text-rose-400 border-rose-500/30 text-[10px]">Admin+</Badge>}
                          {u.role === 'admin' && <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 text-[10px]">Admin</Badge>}
                          {u.is_beta && <Badge className="bg-cyan-500/15 text-cyan-400 border-cyan-500/30 text-[10px]">Beta</Badge>}
                          {u.tier === 'premium' && <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px]">Premium</Badge>}
                        </div>
                        <p className="text-slate-400 text-xs truncate">{u.email}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Signed up {formatDate(u.signed_up)}
                          {u.plan && <span className="ml-2 text-slate-600">({u.plan} plan)</span>}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <div className="text-right">
                          <p className={`text-sm font-bold ${u.total_spent > 0 ? 'text-amber-400' : 'text-slate-500'}`}>
                            £{u.total_spent.toFixed(2)}
                          </p>
                          <p className="text-[10px] text-slate-500">
                            {u.payment_count} payment{u.payment_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                        {u.payment_count > 0 && (
                          expandedSpendUser === u.id
                            ? <ChevronUp className="h-4 w-4 text-amber-400" />
                            : <ChevronDown className="h-4 w-4 text-slate-500" />
                        )}
                      </div>
                    </div>
                    {/* Payment breakdown */}
                    {expandedSpendUser === u.id && u.payments && u.payments.length > 0 && (
                      <div className="mt-2 ml-1 p-2.5 bg-slate-700/30 rounded-lg border border-slate-600/50 space-y-1.5" data-testid={`spend-details-${u.id}`}>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Payment History</p>
                        {u.payments.map((p, i) => (
                          <div key={`${p.date}-${p.amount}-${i}`} className="flex items-center justify-between text-xs">
                            <span className="text-slate-400">
                              {p.plan === 'yearly' ? 'Yearly' : 'Monthly'} subscription
                            </span>
                            <div className="flex items-center gap-3">
                              <span className="text-slate-500 text-[10px]">{formatDate(p.date)}</span>
                              <span className="text-white font-medium">£{(p.amount || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default AdminRevenue;
